/**
 * Normalize hunt.weather_data for display.
 *
 * Expected (current): { morning: { temperature, wind_speed, ... }, ... }
 * Legacy (forecast metrics blob): { temp_day, wind_speed, wind_deg, pop, clouds, ... }
 */

export const HUNT_WEATHER_PERIODS = ['morning', 'midday', 'afternoon', 'all_day'];

const PERIOD_SET = new Set(HUNT_WEATHER_PERIODS);

function isPlainObject(value) {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

export function isPeriodWeatherEntry(value) {
  if (!isPlainObject(value)) return false;
  return (
    value.temperature != null ||
    value.temp_f != null ||
    value.temp != null ||
    value.humidity != null ||
    value.wind_speed != null ||
    value.description != null ||
    value.main != null
  );
}

export function isLegacyDailyWeatherBlob(weatherData) {
  if (!isPlainObject(weatherData)) return false;
  const keys = Object.keys(weatherData);
  if (keys.length === 0) return false;
  if (keys.some((k) => PERIOD_SET.has(String(k).toLowerCase()) && isPeriodWeatherEntry(weatherData[k]))) {
    return false;
  }
  return (
    weatherData.temp_day != null ||
    weatherData.temp_max != null ||
    weatherData.temp_min != null ||
    weatherData.weather_type != null ||
    weatherData.weather_main != null ||
    (weatherData.wind_speed != null && weatherData.wind_deg != null) ||
    weatherData.pop != null
  );
}

function roundOrNull(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : null;
}

function formatWindDirection(degrees) {
  if (degrees == null || degrees === '') return '';
  const n = Number(degrees);
  if (!Number.isFinite(n)) return '';
  const directions = [
    'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
    'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW',
  ];
  return directions[Math.round(n / 22.5) % 16];
}

/**
 * @returns {{ period: string, label: string, data: object }[]}
 */
export function getHuntWeatherPeriods(weatherData) {
  if (!isPlainObject(weatherData)) return [];

  const periods = [];
  for (const key of HUNT_WEATHER_PERIODS) {
    const matchKey = Object.keys(weatherData).find((k) => String(k).toLowerCase() === key);
    if (!matchKey) continue;
    const entry = weatherData[matchKey];
    if (!isPeriodWeatherEntry(entry)) continue;
    periods.push({
      period: key,
      label: key === 'all_day' ? 'All day' : key.charAt(0).toUpperCase() + key.slice(1),
      data: {
        temperature: roundOrNull(entry.temperature ?? entry.temp_f ?? entry.temp),
        wind_speed: roundOrNull(entry.wind_speed),
        wind_direction_text:
          entry.wind_direction_text || formatWindDirection(entry.wind_direction ?? entry.wind_deg),
        humidity: roundOrNull(entry.humidity),
        description: entry.description || entry.main || entry.weather_type || '',
      },
    });
  }

  if (periods.length > 0) return periods;

  if (isLegacyDailyWeatherBlob(weatherData)) {
    const temp =
      roundOrNull(weatherData.temp_day) ??
      roundOrNull(weatherData.temp_max) ??
      roundOrNull(weatherData.temperature);
    const wind = roundOrNull(weatherData.wind_speed);
    const dir = formatWindDirection(weatherData.wind_deg ?? weatherData.wind_direction);
    const humidity = roundOrNull(weatherData.humidity);
    const description =
      weatherData.description ||
      weatherData.weather_main ||
      (weatherData.weather_type ? String(weatherData.weather_type).replace(/_/g, ' ') : '');

    if (temp == null && wind == null && !description) {
      return [];
    }

    return [
      {
        period: 'day',
        label: 'Day',
        data: {
          temperature: temp,
          wind_speed: wind,
          wind_direction_text: dir,
          humidity,
          description,
        },
      },
    ];
  }

  return [];
}

function formatPeriodLine(period) {
  const { label, data } = period;
  const parts = [];
  if (data.temperature != null) parts.push(`${data.temperature}°F`);
  if (data.wind_speed != null) {
    const wind = `${data.wind_speed} mph${data.wind_direction_text ? ` ${data.wind_direction_text}` : ''}`;
    parts.push(wind.trim());
  }
  if (data.description) parts.push(data.description);
  if (parts.length === 0) return null;
  return `${label}: ${parts.join(', ')}`;
}

export function formatHuntWeatherSummary(weatherData) {
  const periods = getHuntWeatherPeriods(weatherData);
  if (periods.length === 0) return 'Weather data not available';
  return periods.map(formatPeriodLine).filter(Boolean).join('; ') || 'Weather data not available';
}
